import { getNewToken } from "../auth/OAuthService";
import { defaultStorage } from "./defaultStorage";

export type OAuthToken = {
  accessToken: string;
  createdAt: number;
  expiresIn: number;
  refreshToken: string;
  scope: string;
  tokenType: "Bearer";
  provider?: string;
  expiresAt?: Date;
};

class TokenStorage {
  private _getPromise: Promise<OAuthToken | null> | null = null;
  private _refreshPromise: Promise<OAuthToken | null> | null = null;

  setExpiry(token: OAuthToken) {
    if (token && token.expiresAt === undefined) {
      const expiresAtMillisecondsEpoch = (token.createdAt + token.expiresIn) * 1000;
      token.expiresAt = new Date(expiresAtMillisecondsEpoch);
    }
  }

  hasExpired(token?: OAuthToken): boolean {
    if (token === undefined || token === null) {
      return true;
    }

    if (token?.expiresAt === null) {
      return false;
    } else {
      // If the token was stored, it's Date type property was stringified, so it needs to be converted back to Date.
      token.expiresAt = token.expiresAt instanceof Date ? token.expiresAt : new Date(token.expiresAt as any);
      return token.expiresAt.getTime() - new Date().getTime() < 0;
    }
  }

  /**
   * Get the OAuth token for the given provider. Only run one request at a time, and wait for it to complete before returning the promise.
   * @param provider
   * @returns
   */
  get(provider: string): Promise<OAuthToken | null> {
    if (!this._getPromise) {
      this._getPromise = this._get(provider).finally(() => {
        this._getPromise = null;
      });
    }

    return this._getPromise;
  }

  private async _get(provider: string) {
    let token = defaultStorage.get<OAuthToken | null>(provider);
    if (!token) {
      return null;
    }

    if (this.hasExpired(token)) {
      if (token.refreshToken === undefined) {
        defaultStorage.remove(provider);
        return null;
      }
      // automatically try to get a new token
      return this.refresh(provider);
    } else {
      return token;
    }
  }

  refresh(provider: string): Promise<OAuthToken | null> {
    if (!this._refreshPromise) {
      this._refreshPromise = this._refresh(provider).finally(() => {
        this._refreshPromise = null;
      });
    }
    return this._refreshPromise;
  }

  private async _refresh(provider: string): Promise<OAuthToken | null> {
    let oAuthData = defaultStorage.get<OAuthToken | null>(provider);

    if (!oAuthData || !oAuthData.refreshToken) {
      return null;
    }

    const token = await getNewToken(oAuthData.refreshToken);
    if (token === null) {
      defaultStorage.remove(provider);
      return null;
    } else {
      return this.add(provider, token);
    }
  }

  remove(provider: string) {
    defaultStorage.remove(provider);
  }

  clear() {
    defaultStorage.clear();
  }

  add(provider: string, token: OAuthToken): OAuthToken {
    if (token) {
      token.provider = provider;
    }

    this.setExpiry(token);

    defaultStorage.add(provider, token);

    return token;
  }
}

export const tokenStorage = new TokenStorage();
